'use client';

import React, { useState, useEffect, useMemo, useCallback, useRef, useImperativeHandle } from 'react';
import { useTranslations } from 'next-intl';
import { Character, CharacterRole } from '../types';
import { useCurrentUser } from '@/contexts/authentication/client';
import { useCharacters, useStoryCharacters } from '@/contexts/characters/hooks';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import { CharacterFormContent } from './CharacterFormContent';
import { CharacterListItem } from './CharacterListItem';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Badge } from '@/shared/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
// Tooltip removed from character list items for performance (was creating
// new TooltipProvider context per item). Using title attr instead.
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import {
  Users,
  Plus,
  User,
  Shield,
  Heart,
  Sparkles,
  AlertTriangle,
  Loader2,
  X,
  Pencil,
  BookOpen,
  Search,
} from 'lucide-react';
import { Input } from '@/shared/components/ui/input';

import { CharacterImportance } from '@/contexts/stories/types';

// Character assignment for story creation
export interface CharacterAssignment {
  characterId: string;
  role: CharacterRole;
  importance: CharacterImportance;
}

interface CharacterSelectorProps {
  selectedCharacterIds: string[];
  onSelectionChange: (characterIds: string[]) => void;
  onAssignmentsChange?: (assignments: CharacterAssignment[]) => void;
  locale: string;
  recentCharacterIds?: string[];
  onRemoveRecent?: (characterId: string) => void;
  /** @deprecated No longer needed - character creation is handled inline */
  onCreateNew?: () => void;
  onCharacterCreated?: (character: Character) => void;
  maxCharacters?: number;
  onNamesChange?: (names: Record<string, string>) => void;
}

/** Imperative handle — lets a parent open the selector straight into its
 *  inline character-creation form (e.g. HeroesStep's "Create new" card). */
export interface CharacterSelectorHandle {
  openCreate: () => void;
}

const ROLE_ICONS: Record<CharacterRole, React.ReactNode> = {
  hero: <Shield className="h-4 w-4" />,
  friend: <Heart className="h-4 w-4" />,
  helper: <Sparkles className="h-4 w-4" />,
  obstacle: <AlertTriangle className="h-4 w-4" />,
};

const getRoleIcon = (role: CharacterRole | null | undefined): React.ReactNode => {
  if (!role) return <User className="h-4 w-4" />;
  return ROLE_ICONS[role] ?? <User className="h-4 w-4" />;
};

const getRoleColor = (role: CharacterRole | null | undefined): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!role) return 'outline';
  return ROLE_COLORS[role] ?? 'outline';
};

const ROLE_COLORS: Record<
  CharacterRole,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  hero: 'default',
  friend: 'secondary',
  helper: 'outline',
  obstacle: 'destructive',
};

// CharacterListItem moved to shared ./CharacterListItem (reused by book hub picker)

export const CharacterSelector = React.forwardRef<
  CharacterSelectorHandle,
  CharacterSelectorProps
>(function CharacterSelector({
  selectedCharacterIds,
  onSelectionChange,
  onAssignmentsChange,
  locale,
  recentCharacterIds = [],
  onRemoveRecent,
  onCreateNew,
  onCharacterCreated,
  maxCharacters = 10,
  onNamesChange,
}: CharacterSelectorProps, ref) {
  const t = useTranslations('stories.characters.selector');
  const tTabs = useTranslations('stories.characters.tabs');
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null);
  const [isFormBusy, setIsFormBusy] = useState(false);

  const { user } = useCurrentUser();
  const familyId = user?.familyId ?? undefined;

  // Keep the fetch enabled whenever there's anything to display characters for:
  // sheet open, recent-chip suggestions, OR selected IDs (otherwise closing the
  // sheet as a first-time user with no recents empties the preview block above
  // the trigger — IDs persist but sortedCharacters clears → selectedCharacters = []).
  const shouldFetch = isOpen || recentCharacterIds.length > 0 || selectedCharacterIds.length > 0;
  const { data: characters = [], isFetching, error, refetch } = useCharacters(
    shouldFetch ? familyId : undefined,
  );
  // Book-scoped ("from stories") characters. Fetch on the SAME condition as the
  // library list (not just while the sheet is open): a selected story char must
  // still resolve for the preview block above the trigger once the sheet closes,
  // otherwise it vanishes from the card until the sheet is reopened.
  const { data: storyGroups = [], isFetching: storyFetching } = useStoryCharacters(
    shouldFetch ? familyId : undefined,
  );
  const isWaitingForAuth = !familyId && shouldFetch;
  const showLoading = isFetching || isWaitingForAuth;

  const sortedCharacters = useMemo(() => {
    return [...characters].sort((a, b) => a.name.localeCompare(b.name));
  }, [characters]);

  const [selectorTab, setSelectorTab] = useState<'library' | 'story'>('library');
  const [storyVisible, setStoryVisible] = useState(20);
  const [charSearch, setCharSearch] = useState('');

  const storyCharacterCount = useMemo(
    () => storyGroups.reduce((sum, g) => sum + g.characters.length, 0),
    [storyGroups],
  );

  const matchesSearch = useCallback((c: Character, q: string) =>
    c.name.toLowerCase().includes(q) ||
    !!c.physicalDescription?.toLowerCase().includes(q),
  []);

  // Search filters BOTH tabs by name/description.
  const filteredLibrary = useMemo(() => {
    const q = charSearch.trim().toLowerCase();
    if (!q) return sortedCharacters;
    return sortedCharacters.filter((c) => matchesSearch(c, q));
  }, [sortedCharacters, charSearch, matchesSearch]);

  const filteredStoryGroups = useMemo(() => {
    const q = charSearch.trim().toLowerCase();
    if (!q) return storyGroups;
    return storyGroups
      .map((g) => ({ ...g, characters: g.characters.filter((c) => matchesSearch(c, q)) }))
      .filter((g) => g.characters.length > 0);
  }, [storyGroups, charSearch, matchesSearch]);

  const filteredStoryCount = useMemo(
    () => filteredStoryGroups.reduce((sum, g) => sum + g.characters.length, 0),
    [filteredStoryGroups],
  );

  // Client-side "show more" for the story tab (data already loaded).
  const shownStoryGroups = useMemo(() => {
    const out: typeof filteredStoryGroups = [];
    let budget = storyVisible;
    for (const g of filteredStoryGroups) {
      if (budget <= 0) break;
      const slice = g.characters.slice(0, budget);
      out.push({ ...g, characters: slice });
      budget -= slice.length;
    }
    return out;
  }, [filteredStoryGroups, storyVisible]);
  const storyHasMore = filteredStoryCount > storyVisible;

  // Combined id→character pool (library + story) so a selected story char still
  // resolves in the selection preview, footer count and names map.
  const allCharactersById = useMemo(() => {
    const map = new Map<string, Character>();
    for (const c of sortedCharacters) map.set(c.id, c);
    for (const g of storyGroups) for (const c of g.characters) if (!map.has(c.id)) map.set(c.id, c);
    return map;
  }, [sortedCharacters, storyGroups]);

  const [characterRoles, setCharacterRoles] = useState<Record<string, CharacterRole>>({});
  const selectedIdsSet = useMemo(() => new Set(selectedCharacterIds), [selectedCharacterIds]);
  const isAtLimit = selectedCharacterIds.length >= maxCharacters;

  // Reset view to list when sheet closes
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setView('list');
        setEditingCharacter(null);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleToggleCharacter = useCallback((characterId: string, checked: boolean) => {
    const currentSet = new Set(selectedCharacterIds);
    if (checked) {
      if (currentSet.size >= maxCharacters) return;
      currentSet.add(characterId);
    } else {
      currentSet.delete(characterId);
    }
    const newArray = Array.from(currentSet);
    if (newArray.length !== selectedCharacterIds.length || !newArray.every(id => selectedIdsSet.has(id))) {
      onSelectionChange(newArray);
    }
  }, [selectedCharacterIds, selectedIdsSet, onSelectionChange, maxCharacters]);

  const handleClearSelection = useCallback(() => {
    if (selectedCharacterIds.length > 0) {
      onSelectionChange([]);
    }
  }, [selectedCharacterIds.length, onSelectionChange]);

  const selectedCharacters = useMemo(() => {
    return selectedCharacterIds
      .map((id) => allCharactersById.get(id))
      .filter((c): c is Character => !!c);
  }, [selectedCharacterIds, allCharactersById]);

  const recentCharacters = useMemo(() => {
    const recentSet = new Set(recentCharacterIds);
    return sortedCharacters.filter((c) => recentSet.has(c.id) && !selectedIdsSet.has(c.id));
  }, [sortedCharacters, recentCharacterIds, selectedIdsSet]);

  const handleQuickSelect = useCallback((characterId: string) => {
    if (!selectedIdsSet.has(characterId) && selectedCharacterIds.length < maxCharacters) {
      onSelectionChange([...selectedCharacterIds, characterId]);
    }
  }, [selectedCharacterIds, selectedIdsSet, onSelectionChange, maxCharacters]);

  const handleRoleChange = useCallback((characterId: string, role: CharacterRole) => {
    setCharacterRoles((prev) => ({ ...prev, [characterId]: role }));
  }, []);

  const getCharacterRole = useCallback((characterId: string, index: number): CharacterRole => {
    if (characterRoles[characterId]) return characterRoles[characterId];
    return index === 0 ? CharacterRole.Hero : CharacterRole.Friend;
  }, [characterRoles]);

  const getImportanceFromRole = (role: CharacterRole): CharacterImportance => {
    switch (role) {
      case CharacterRole.Hero: return CharacterImportance.Main;
      case CharacterRole.Friend:
      case CharacterRole.Helper: return CharacterImportance.Supporting;
      case CharacterRole.Obstacle: return CharacterImportance.Minor;
      default: return CharacterImportance.Supporting;
    }
  };

  const currentAssignments = useMemo(() => {
    return selectedCharacterIds.map((characterId, index) => ({
      characterId,
      role: getCharacterRole(characterId, index),
      importance: getImportanceFromRole(getCharacterRole(characterId, index)),
    }));
  }, [selectedCharacterIds, getCharacterRole]);

  const prevAssignmentsRef = useRef<string>('');

  useEffect(() => {
    if (!onAssignmentsChange) return;
    const assignmentsJson = JSON.stringify(currentAssignments);
    if (assignmentsJson !== prevAssignmentsRef.current) {
      prevAssignmentsRef.current = assignmentsJson;
      onAssignmentsChange(currentAssignments);
    }
  }, [currentAssignments, onAssignmentsChange]);

  useEffect(() => {
    setCharacterRoles((prev) => {
      const selectedSet = new Set(selectedCharacterIds);
      const newRoles: Record<string, CharacterRole> = {};
      for (const [id, role] of Object.entries(prev)) {
        if (selectedSet.has(id)) newRoles[id] = role;
      }
      return newRoles;
    });
  }, [selectedCharacterIds]);

  const prevNamesRef = useRef<string>('');
  useEffect(() => {
    if (!onNamesChange) return;
    const names: Record<string, string> = {};
    for (const c of selectedCharacters) names[c.id] = c.name;
    const namesJson = JSON.stringify(names);
    if (namesJson !== prevNamesRef.current) {
      prevNamesRef.current = namesJson;
      onNamesChange(names);
    }
  }, [selectedCharacters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateNew = useCallback(() => {
    setEditingCharacter(null);
    setView('create');
  }, []);

  // Parent-driven entry into the inline create form (opens the sheet on it).
  useImperativeHandle(ref, () => ({
    openCreate: () => {
      setEditingCharacter(null);
      setView('create');
      setIsOpen(true);
    },
  }), []);

  const handleEditCharacter = useCallback((character: Character) => {
    setEditingCharacter(character);
    setView('edit');
  }, []);

  const handleCharacterCreatedInline = useCallback((char: Character) => {
    // Auto-select the new character
    if (selectedCharacterIds.length < maxCharacters && !selectedIdsSet.has(char.id)) {
      onSelectionChange([...selectedCharacterIds, char.id]);
    }
    refetch();
    onCharacterCreated?.(char);
    // Don't switch to list — stay in create/edit view so user sees portrait generation progress.
    // User can manually close or go back when ready.
    // View will be 'create' but CharacterFormContent transitions to edit mode internally via localCharacter.
  }, [selectedCharacterIds, maxCharacters, selectedIdsSet, onSelectionChange, refetch, onCharacterCreated]);

  const handleCharacterEditedInline = useCallback((char: Character) => {
    refetch();
    setEditingCharacter(char);
  }, [refetch]);

  const handleFormBusyChange = useCallback((busy: boolean) => {
    setIsFormBusy(busy);
  }, []);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    // Block close while form is busy
    if (!newOpen && isFormBusy) return;
    setIsOpen(newOpen);
  }, [isFormBusy]);

  return (
    <div className="space-y-2" data-testid="character-selector">
      <Label>{t('label')}</Label>

      {/* Selected characters preview with role selector */}
      {selectedCharacters.length > 0 && (
        <div className="space-y-2 rounded-md border p-3" data-testid="character-selected-list">
          {selectedCharacters.map((character, index) => {
            const assignedRole = getCharacterRole(character.id, index);
            return (
              <div key={character.id} className="flex items-center gap-2 justify-between">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Avatar className="h-8 w-8 shrink-0">
                    {(character.primaryPortraitUrl || character.primaryImageUrl) && (
                      <AvatarImage
                        src={mediaSrc(character.primaryPortraitUrl || character.primaryImageUrl)}
                        alt={character.name}
                      />
                    )}
                    <AvatarFallback>{character.name.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <Badge variant={getRoleColor(assignedRole)} className="gap-1 whitespace-nowrap shrink-0">
                    {getRoleIcon(assignedRole)}
                    <span className="truncate max-w-[120px]">{character.name}</span>
                  </Badge>
                </div>
                <div className="flex items-center gap-1">
                  <Select
                    value={assignedRole}
                    onValueChange={(value) => handleRoleChange(character.id, value as CharacterRole)}
                  >
                    <SelectTrigger className="h-7 w-auto text-xs" data-testid={`character-role-select-${character.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hero">
                        <span className="flex items-center gap-1"><Shield className="h-3 w-3" />{t('role.hero')}</span>
                      </SelectItem>
                      <SelectItem value="friend">
                        <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{t('role.friend')}</span>
                      </SelectItem>
                      <SelectItem value="helper">
                        <span className="flex items-center gap-1"><Sparkles className="h-3 w-3" />{t('role.helper')}</span>
                      </SelectItem>
                      <SelectItem value="obstacle">
                        <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{t('role.obstacle')}</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 hover:text-destructive"
                    onClick={() => handleToggleCharacter(character.id, false)}
                    data-testid={`character-remove-${character.id}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent characters quick selection */}
      {recentCharacters.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Plus className="h-3 w-3" />
            {t('recentlyUsed')}
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="character-recent-list">
            {recentCharacters.map((character) => (
              <Badge
                key={character.id}
                variant="outline"
                className={`gap-1.5 pl-0.5 cursor-pointer hover:bg-accent transition-colors whitespace-nowrap shrink-0 w-auto text-xs ${isAtLimit ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !isAtLimit && handleQuickSelect(character.id)}
                data-testid={`character-recent-${character.id}`}
              >
                <Avatar className="h-5 w-5">
                  {(character.primaryPortraitUrl || character.primaryImageUrl) && (
                    <AvatarImage
                      src={mediaSrc(character.primaryPortraitUrl || character.primaryImageUrl)}
                      alt={character.name}
                    />
                  )}
                  <AvatarFallback className="text-[10px]">
                    {character.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <Plus className="h-3 w-3" />
                {character.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Sheet — nested inside wizard Dialog. onInteractOutside always prevented
           to stop overlay dismiss from propagating to parent Dialog. Close via Done/ESC only. */}
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>
          <Button type="button" variant="outline" className="w-full gap-2" data-testid="character-selector-trigger">
            <Users className="h-4 w-4" />
            {selectedCharacters.length > 0
              ? t('editSelection', { count: selectedCharacters.length })
              : t('moreCharacters')}
          </Button>
        </SheetTrigger>

        <SheetContent
          side="right"
          className={`w-full ${view !== 'list' ? 'sm:max-w-2xl' : 'sm:max-w-xl'}`}
          onInteractOutside={(e) => { e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (isFormBusy) e.preventDefault(); }}
        >
          {view === 'create' || view === 'edit' ? (
            <>
              <SheetHeader>
                <SheetTitle>
                  {view === 'edit' ? t('editCharacterTitle') : t('createNewTitle')}
                </SheetTitle>
                <SheetDescription>
                  {view === 'edit' ? t('editCharacterDescription') : t('createNewDescription')}
                </SheetDescription>
              </SheetHeader>
              <CharacterFormContent
                character={view === 'edit' ? editingCharacter : null}
                onSuccess={view === 'edit' ? handleCharacterEditedInline : handleCharacterCreatedInline}
                onCancel={() => setView('list')}
                isActive={view === 'create' || view === 'edit'}
                onBusyChange={handleFormBusyChange}
              />
            </>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle>{t('title')}</SheetTitle>
                <SheetDescription>{t('description')}</SheetDescription>
              </SheetHeader>

              {showLoading ? (
                <div className="flex items-center justify-center py-12 px-4">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : error ? (
                <div className="px-4">
                  <Alert variant="destructive">
                    <AlertDescription>{error.message}</AlertDescription>
                  </Alert>
                </div>
              ) : sortedCharacters.length === 0 && storyCharacterCount === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <div className="mb-4 rounded-full bg-muted p-6">
                    <User className="h-12 w-12 text-muted-foreground" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold">{t('noCharacters')}</h3>
                  <p className="mb-6 max-w-md text-muted-foreground">
                    {t('noCharactersMessage')}
                  </p>
                  <Button
                    type="button"
                    variant="default"
                    className="gap-2"
                    onClick={handleCreateNew}
                    data-testid="character-create-link"
                  >
                    <Plus className="h-4 w-4" />
                    {t('createNew')}
                  </Button>
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin px-4 space-y-4">
                  {/* Search — filters both tabs by name/description */}
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={charSearch}
                      onChange={(e) => setCharSearch(e.target.value)}
                      placeholder={t('searchPlaceholder')}
                      className="pl-8"
                      data-testid="character-selector-search"
                    />
                    {charSearch && (
                      <button
                        type="button"
                        onClick={() => setCharSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={t('clearSearch')}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <Tabs value={selectorTab} onValueChange={(v) => setSelectorTab(v as 'library' | 'story')}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="library" data-testid="selector-tab-library">
                        {tTabs('library')}
                      </TabsTrigger>
                      <TabsTrigger value="story" data-testid="selector-tab-story">
                        {tTabs('fromStories')}
                        {storyCharacterCount > 0 && (
                          <span className="ml-1.5 rounded-full bg-muted px-1.5 text-xs">{storyCharacterCount}</span>
                        )}
                      </TabsTrigger>
                    </TabsList>

                    {/* Library tab */}
                    <TabsContent value="library" className="mt-4 space-y-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCreateNew}
                        className="gap-2 w-full"
                        data-testid="character-create-new-btn"
                      >
                        <Plus className="h-4 w-4" />
                        {t('createNew')}
                      </Button>

                      {filteredLibrary.length === 0 ? (
                        <p className="py-6 text-center text-sm text-muted-foreground">
                          {charSearch ? t('noSearchResults') : t('noCharactersMessage')}
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {filteredLibrary.map((character) => (
                            <CharacterListItem
                              key={character.id}
                              character={character}
                              isSelected={selectedIdsSet.has(character.id)}
                              isAtLimit={isAtLimit}
                              onToggle={handleToggleCharacter}
                              onEdit={handleEditCharacter}
                              t={t}
                            />
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    {/* "From stories" tab — book-scoped chars grouped by book,
                        selectable (virtual select), edit hidden (readOnly). */}
                    <TabsContent value="story" className="mt-4 space-y-6">
                      {storyFetching && storyGroups.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                      ) : storyGroups.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                          <div className="mb-4 rounded-full bg-muted p-6">
                            <BookOpen className="h-12 w-12 text-muted-foreground" />
                          </div>
                          <p className="max-w-md text-muted-foreground">{tTabs('storyEmpty')}</p>
                        </div>
                      ) : filteredStoryGroups.length === 0 ? (
                        <p className="py-6 text-center text-sm text-muted-foreground">
                          {t('noSearchResults')}
                        </p>
                      ) : (
                        <>
                          {shownStoryGroups.map((group) => (
                            <section key={group.bookId} className="space-y-3" data-testid={`selector-story-illustration-${group.bookId}`}>
                              <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                <BookOpen className="h-4 w-4 shrink-0" />
                                <span className="truncate">
                                  {group.bookTitle
                                    ? tTabs('fromBook', { title: group.bookTitle })
                                    : tTabs('fromStories')}
                                </span>
                              </h4>
                              <div className="space-y-4">
                                {group.characters.map((character) => (
                                  <CharacterListItem
                                    key={character.id}
                                    character={character}
                                    isSelected={selectedIdsSet.has(character.id)}
                                    isAtLimit={isAtLimit}
                                    onToggle={handleToggleCharacter}
                                    onEdit={handleEditCharacter}
                                    t={t}
                                    readOnly
                                  />
                                ))}
                              </div>
                            </section>
                          ))}

                          {storyHasMore && (
                            <div className="flex justify-center">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                data-testid="selector-story-load-more"
                                onClick={() => setStoryVisible((v) => v + 20)}
                              >
                                {tTabs('loadMore')}
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </TabsContent>
                  </Tabs>

                  {/* Actions (shared across tabs) */}
                  <div className="flex flex-col gap-3 border-t pt-4">
                    {isAtLimit && (
                      <p className="text-xs text-warning">{t('errors.maxCharactersReached', { max: maxCharacters })}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        {selectedCharacters.length > 0
                          ? t('selectedCount', { count: selectedCharacters.length, max: maxCharacters })
                          : t('noneSelected')}
                      </div>
                      <div className="flex gap-2">
                        {selectedCharacters.length > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleClearSelection}
                            data-testid="character-clear-btn"
                          >
                            {t('clearSelection')}
                          </Button>
                        )}
                        <Button type="button" size="sm" onClick={() => setIsOpen(false)} data-testid="character-done-btn">
                          {t('done')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      <p className="text-xs text-muted-foreground">{t('help')}</p>
    </div>
  );
});
